<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { computed, nextTick, onBeforeUnmount, ref } from "vue";

import { backendOld } from "~/api";
import Button from "~/components/dumb/Button.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { notify } from "~/components/notifications";
import { AUDIO_MIME_TYPE } from "~/constants/speechToText";
import { CheckIcon, LoadingIcon, MicrophoneIcon, XIcon } from "~/icons";
import { ButtonSize, ButtonStyle, IconSize, NotificationType } from "~/shared/enums";
import { usePageStore } from "~/stores";
import { makeDuid } from "~/utils/common";

const WAVE_GIF_URL = new URL("/static/img/sound_wave_animation.gif", import.meta.url).href;

const emit = defineEmits<{
  result: [text: string];
}>();

const pageStore = usePageStore();

const stream = ref<MediaStream | null>(null);
const recorder = ref<MediaRecorder | null>(null);

const microphoneDisabled = ref(false);
const isRecording = ref(false);
const isTranscribing = ref(false);

const activeRunDuid = ref<string | null>(null);

const stopTracks = () => {
  const tracks = stream.value?.getTracks?.() ?? [];
  tracks.forEach((t) => {
    t.stop();
  });
  stream.value = null;
};

const reset = () => {
  isRecording.value = false;
  recorder.value = null;
  stopTracks();
};

const transcribe = async (blob: Blob, runDuid: string) => {
  try {
    const { data } = await backendOld.speechToText.transcribe(blob);

    if (activeRunDuid.value !== runDuid) {
      return;
    }

    emit("result", data.text ?? "");
  } catch (error) {
    notify({
      message: "Speech to text transcription failed",
      type: NotificationType.ERROR,
    });
    Sentry.captureException(error);
  } finally {
    nextTick(() => {
      if (activeRunDuid.value !== runDuid) {
        return;
      }

      isTranscribing.value = false;
      activeRunDuid.value = null;
      reset();
    });
  }
};

const handleRecorderStop = (chunks: BlobPart[]) => {
  const blob = new Blob(chunks, { type: AUDIO_MIME_TYPE });
  stopTracks();

  if (!activeRunDuid.value) {
    reset();
    return;
  }
  transcribe(blob, activeRunDuid.value);
};

const createRecorder = (mediaStream: MediaStream) => {
  const chunks: BlobPart[] = [];
  const rec = new MediaRecorder(mediaStream, { mimeType: AUDIO_MIME_TYPE });

  rec.ondataavailable = (event: BlobEvent) => {
    if (event.data?.size) {
      chunks.push(event.data);
    }
  };

  rec.onstop = () => handleRecorderStop(chunks);

  return rec;
};

const startRecording = async () => {
  if (isRecording.value || isTranscribing.value) {
    return;
  }
  microphoneDisabled.value = false;
  try {
    isRecording.value = true;
    stream.value = await navigator.mediaDevices.getUserMedia({ audio: true });

    recorder.value = createRecorder(stream.value);
    recorder.value.start();
    // TODO voice add a visual indicator of recording
  } catch (error) {
    microphoneDisabled.value = true;
    reset();
    Sentry.captureException(error);
    notify({
      message: "Failed to start recording",
      type: NotificationType.ERROR,
    });
  }
};

const cancelRecording = () => {
  if (!isRecording.value && !isTranscribing.value) {
    return;
  }
  activeRunDuid.value = null;
  isTranscribing.value = false;
  isRecording.value = false;

  if (recorder.value?.state === "inactive") {
    reset();
    return;
  }
  recorder.value?.stop?.();
};

const finishRecording = () => {
  if (!isRecording.value) {
    return;
  }
  const runDuid = makeDuid();
  activeRunDuid.value = runDuid;

  isTranscribing.value = true;
  isRecording.value = false;
  recorder.value?.stop?.();
};

onBeforeUnmount(reset);

defineExpose({
  working: computed(() => isRecording.value || isTranscribing.value),
});
</script>

<template>
  <div v-if="pageStore.showAlpha" class="flex items-center gap-1">
    <Tooltip v-if="!isRecording && !isTranscribing" text="Convert speech to text">
      <Button
        :btn-style="ButtonStyle.SECONDARY"
        :icon="MicrophoneIcon"
        :icon-size="IconSize.S"
        :icon-args="{ class: 'text-vlt' }"
        :size="ButtonSize.CHIP"
        a11y-label="Convert speech to text"
        borderless
        @click="startRecording" />
    </Tooltip>

    <!-- Recording: show cancel, meter, finish -->
    <template v-else-if="isRecording || isTranscribing">
      <Tooltip text="Cancel recording">
        <Button
          :btn-style="ButtonStyle.SECONDARY"
          :icon="XIcon"
          :size="ButtonSize.SMALL"
          :icon-args="{ class: 'text-vlt' }"
          class="px-1.5 py-[3px]"
          a11y-label="Cancel recording"
          @click="cancelRecording" />
      </Tooltip>

      <!-- TODO voice add a visual indicator of recording, delete this animation -->
      <div class="flex h-6 w-9 items-center justify-center" aria-hidden="true">
        <img v-if="isRecording" :src="WAVE_GIF_URL" alt="Sound wave" class="object-contain" />
        <LoadingIcon v-else class="animate-spin text-vlt icon-sm" />
      </div>

      <Tooltip text="Transcribe your speech and insert it at the cursor">
        <Button
          :btn-style="ButtonStyle.RECOMMENDATION"
          :icon="CheckIcon"
          :size="ButtonSize.SMALL"
          a11y-label="Convert recording to text"
          class="px-1.5 py-[3px]"
          :disabled="isTranscribing"
          @click="finishRecording" />
      </Tooltip>
    </template>
  </div>
</template>
